import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import i18n from '../i18n'
import {
  fetchNotifications,
  markNotificationsRead,
  fetchNotificationPreferences,
  updateNotificationPreferences,
} from '../api'
import BackButton from './BackButton'
import Icon from './Icon'
import { thumbUrl } from '../lib/imageThumb'

// ── Helpers ───────────────────────────────────────────────────────────────────

function timeAgo(isoString) {
  const diff = Math.floor((Date.now() - new Date(isoString).getTime()) / 1000)
  if (diff < 60)  return i18n.t('time.justNow', { ns: 'common' })
  if (diff < 3600) return i18n.t('time.mAgo', { ns: 'common', count: Math.floor(diff / 60) })
  if (diff < 86400) return i18n.t('time.hAgo', { ns: 'common', count: Math.floor(diff / 3600) })
  return i18n.t('time.dAgo', { ns: 'common', count: Math.floor(diff / 86400) })
}

// Stroke icon per type (same as mobile NotifRow).
const TYPE_ICONS = {
  dm_message:              'chatbubble-outline',
  event_message:           'chatbox-outline',
  event_join:              'people-outline',
  new_event:               'flash-outline',
  mention:                 'at-outline',
  channel_message:         'chatbubbles-outline',
  city_join:               'person-add-outline',
  friend_request_received: 'person-add-outline',
  friend_request_accepted: 'people-outline',
  friend_added:            'person-add-outline',  // legacy
  vibe_received:           'star-outline',
  profile_view:            'eye-outline',
  challenge_invitation:    'flame-outline',
  challenge_takeon_request:'flame-outline',
  daily_vote:              'heart-outline',
  daily_twist_completed:   'leaf-outline',
  daily_twist_picked:      'leaf-outline',
  photo_comment:           'chatbubble-ellipses-outline',
  photo_mention:           'at-outline',
}

// ── Toggle component ──────────────────────────────────────────────────────────

function Toggle({ checked, onChange, disabled }) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      className={`notif-toggle${checked ? ' on' : ''}`}
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
    />
  )
}

// ── Main component ────────────────────────────────────────────────────────────

const PREVIEW_LIMIT = 5

export default function NotificationsScreen({ onBack, onNavigate, onUnreadChange, account, incoming }) {
  const { t } = useTranslation('notifications')
  const [notifications, setNotifications] = useState([])
  const [loading, setLoading]             = useState(true)
  const [loadingAll, setLoadingAll]       = useState(false)
  const [showAll, setShowAll]             = useState(false)
  const [unreadCount, setUnreadCount]     = useState(0)
  const [prefs, setPrefs]                 = useState(null)
  const [prefsSaving, setPrefsSaving]     = useState(false)

  // A notification pushed live over the WebSocket while this screen is open (prompt 12).
  useEffect(() => {
    if (!incoming?.id) return
    setNotifications(prev => prev.some(n => n.id === incoming.id) ? prev : [incoming, ...prev])
    setUnreadCount(c => c + 1)
  }, [incoming])

  useEffect(() => {
    if (!account) return  // preferences require a registered account

    let cancelled = false
    setLoading(true)
    fetchNotifications({ limit: PREVIEW_LIMIT })
      .then(data => {
        if (cancelled) return
        setNotifications(data.notifications)
        setUnreadCount(data.unread_count)
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false) })

    fetchNotificationPreferences()
      .then(data => { if (!cancelled) setPrefs(data.preferences) })
      .catch((err) => { console.error('[notifications] preferences fetch failed:', err) })

    return () => { cancelled = true }
  }, [account])

  function handleSeeAll() {
    if (showAll) return
    setLoadingAll(true)
    fetchNotifications({ limit: 100 })
      .then(data => {
        setNotifications(data.notifications)
        setShowAll(true)
      })
      .catch(() => {})
      .finally(() => setLoadingAll(false))
  }

  function handleMarkAllRead() {
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })))
    setUnreadCount(0)
    onUnreadChange(0)
    markNotificationsRead(true)
  }

  function handleClickNotif(notif) {
    if (!notif.is_read) {
      setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, is_read: true } : n))
      setUnreadCount(prev => Math.max(0, prev - 1))
      onUnreadChange(Math.max(0, unreadCount - 1))
      markNotificationsRead([notif.id])
    }
    onNavigate(notif)
  }

  async function handleTogglePref(key, value) {
    const next = { ...prefs, [key]: value }
    setPrefs(next)
    setPrefsSaving(true)
    try {
      const data = await updateNotificationPreferences(next)
      setPrefs(data.preferences)
    } catch {
      setPrefs(prefs) // revert on error
    } finally {
      setPrefsSaving(false)
    }
  }

  return (
    <div className="full-page notif-page">
      <div className="page-header">
        <BackButton onClick={onBack} />
        <span className="page-title">{t('title')}</span>
        {unreadCount > 0 && (
          <button className="notif-mark-all" onClick={handleMarkAllRead}>
            {t('markAllRead')}
          </button>
        )}
      </div>

      <div className="page-body notif-body">
        {/* ── Notification list ── */}
        {loading ? (
          <div className="notif-loading">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="notif-skeleton">
                <div className="skel skel-icon" />
                <div className="notif-skeleton-text">
                  <div className="skel skel-line" style={{ width: `${55 + (i * 13) % 35}%` }} />
                  <div className="skel skel-line skel-line--sm" style={{ width: `${40 + (i * 11) % 30}%` }} />
                </div>
              </div>
            ))}
          </div>
        ) : notifications.length === 0 ? (
          <div className="notif-empty">
            <p className="notif-empty-icon" style={{ color: 'var(--muted2)' }}><Icon name="notifications-outline" size={32} /></p>
            <p className="notif-empty-title">{t('empty.title')}</p>
            <p className="notif-empty-sub">{t('empty.sub')}</p>
          </div>
        ) : (
          <div className="notif-list">
            {notifications.map(n => (
              <button
                key={n.id}
                className={`notif-row${n.is_read ? ' read' : ''}`}
                onClick={() => handleClickNotif(n)}
              >
                {/* A comment: who wrote it (the icon on it), the photo's thumbnail at the end. */}
                {n.data?.actorPhoto
                  ? <span className="notif-actor"><img src={n.data.actorPhoto} alt="" /><Icon name={TYPE_ICONS[n.type]} size={12} /></span>
                  : <span className="notif-icon" style={{ color: n.is_read ? 'var(--muted2)' : 'var(--primary-text)' }}><Icon name={TYPE_ICONS[n.type] ?? 'notifications-outline'} size={20} /></span>}
                <div className="notif-content">
                  <span className="notif-title">{n.title}</span>
                  {n.body && <span className="notif-body-text">{n.body}</span>}
                  <span className="notif-time">{timeAgo(n.created_at)}</span>
                </div>
                {n.data?.photoThumb && <img loading="lazy" className="notif-thumb" src={thumbUrl(n.data.photoThumb)} alt="" />}
                {!n.is_read && <span className="notif-unread-dot" />}
              </button>
            ))}
          </div>
        )}

        {/* ── See all CTA ── */}
        {!loading && !showAll && (
          <button
            className="notif-see-all"
            onClick={handleSeeAll}
            disabled={loadingAll}
          >
            {loadingAll ? t('loading') : t('seeAll')}
          </button>
        )}

        {/* ── Preferences ──
            DM, event-chat, and city-chat toggles live on the Conversations
            screen now - they govern the envelope icon's behaviour, not the bell. */}
        <div className="notif-prefs">
          <div className="notif-prefs-title">{t('prefs.title')}</div>

          <div className="notif-pref-row">
            <div className="notif-pref-label">
              <span className="notif-pref-name">{t('prefs.mentionName')}</span>
              <span className="notif-pref-desc">{t('prefs.mentionDesc')}</span>
            </div>
            <Toggle
              checked={prefs?.mention_push ?? true}
              onChange={v => handleTogglePref('mention_push', v)}
              disabled={prefsSaving || !prefs}
            />
          </div>

          <div className="notif-pref-row">
            <div className="notif-pref-label">
              <span className="notif-pref-name">{t('prefs.commentName')}</span>
              <span className="notif-pref-desc">{t('prefs.commentDesc')}</span>
            </div>
            <Toggle
              checked={prefs?.comment_push ?? true}
              onChange={v => handleTogglePref('comment_push', v)}
              disabled={prefsSaving || !prefs}
            />
          </div>

          <div className="notif-pref-row">
            <div className="notif-pref-label">
              <span className="notif-pref-name">{t('prefs.newEventName')}</span>
              <span className="notif-pref-desc">{t('prefs.newEventDesc')}</span>
            </div>
            <Toggle
              checked={prefs?.new_event_push ?? false}
              onChange={v => handleTogglePref('new_event_push', v)}
              disabled={prefsSaving || !prefs}
            />
          </div>

          <div className="notif-pref-row">
            <div className="notif-pref-label">
              <span className="notif-pref-name">{t('prefs.newChallengeName')}</span>
              <span className="notif-pref-desc">{t('prefs.newChallengeDesc')}</span>
            </div>
            <Toggle
              checked={prefs?.new_challenge_push ?? true}
              onChange={v => handleTogglePref('new_challenge_push', v)}
              disabled={prefsSaving || !prefs}
            />
          </div>

          <div className="notif-pref-row">
            <div className="notif-pref-label">
              <span className="notif-pref-name">{t('prefs.cityJoinName')}</span>
              <span className="notif-pref-desc">{t('prefs.cityJoinDesc')}</span>
            </div>
            <Toggle
              checked={prefs?.city_join_push ?? false}
              onChange={v => handleTogglePref('city_join_push', v)}
              disabled={prefsSaving || !prefs}
            />
          </div>

          <div className="notif-pref-row">
            <div className="notif-pref-label">
              <span className="notif-pref-name">{t('prefs.worldArrivalName')}</span>
              <span className="notif-pref-desc">{t('prefs.worldArrivalDesc')}</span>
            </div>
            <Toggle
              checked={prefs?.world_arrival_push ?? false}
              onChange={v => handleTogglePref('world_arrival_push', v)}
              disabled={prefsSaving || !prefs}
            />
          </div>

          <div className="notif-pref-row">
            <div className="notif-pref-label">
              <span className="notif-pref-name">{t('prefs.friendName')}</span>
              <span className="notif-pref-desc">{t('prefs.friendDesc')}</span>
            </div>
            <Toggle
              checked={prefs?.friend_request_push ?? true}
              onChange={v => handleTogglePref('friend_request_push', v)}
              disabled={prefsSaving || !prefs}
            />
          </div>

          <div className="notif-pref-row">
            <div className="notif-pref-label">
              <span className="notif-pref-name">{t('prefs.notesName')}</span>
              <span className="notif-pref-desc">{t('prefs.notesDesc')}</span>
            </div>
            <Toggle
              checked={prefs?.vibe_received_push ?? true}
              onChange={v => handleTogglePref('vibe_received_push', v)}
              disabled={prefsSaving || !prefs}
            />
          </div>

          <div className="notif-pref-row">
            <div className="notif-pref-label">
              <span className="notif-pref-name">{t('prefs.profileViewName')}</span>
              <span className="notif-pref-desc">{t('prefs.profileViewDesc')}</span>
            </div>
            <Toggle
              checked={prefs?.profile_view_push ?? true}
              onChange={v => handleTogglePref('profile_view_push', v)}
              disabled={prefsSaving || !prefs}
            />
          </div>

          <div className="notif-pref-row">
            <div className="notif-pref-label">
              <span className="notif-pref-name">{t('prefs.announcementName')}</span>
              <span className="notif-pref-desc">{t('prefs.announcementDesc')}</span>
            </div>
            <Toggle
              checked={prefs?.admin_announcement_push ?? true}
              onChange={v => handleTogglePref('admin_announcement_push', v)}
              disabled={prefsSaving || !prefs}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
